import { calculateCenterOfPolygon } from 'utils/calculateCenterOfPolygon';
import { calculateClosestStation } from 'services/calculateClosestStation';
import type { WeatherStation } from 'contexts/ClosestStationContext';
import { firstPolygon } from 'utils/multiPolygonHelper';

interface Lv95Coordinate {
  x: number;
  y: number;
}

interface GridLike {
  params?: {
    analysisArea?: Lv95Coordinate[] | unknown;
    [key: string]: unknown;
  } | null;
  [key: string]: unknown;
}

interface WindSubSimulationParams {
  referenceHeight?: unknown;
  flowDir?: unknown;
  referenceVelocity?: unknown;
  [key: string]: unknown;
}

interface SubSimulationLike {
  simType?: string;
  params?: WindSubSimulationParams;
}

const getAnalysisArea = (grid: GridLike | null | undefined): Lv95Coordinate[] | null => {
  const analysisArea = grid?.params?.analysisArea;
  const polygon = firstPolygon<Lv95Coordinate>(analysisArea);
  return polygon.length > 0 ? polygon : null;
};

const getClosestStationFromGrid = (grid: GridLike | null | undefined) => {
  const analysisArea = getAnalysisArea(grid);
  if (!analysisArea || analysisArea.length === 0) return null;

  const centerOfPolygon = calculateCenterOfPolygon(analysisArea);
  if (!centerOfPolygon) return null;

  return calculateClosestStation(centerOfPolygon);
};

const shouldUpdateStationDerivedValue = (currentValue: unknown, previousValue: unknown) =>
  currentValue == null || currentValue === '' || currentValue === previousValue;

const hydrateWindSubSimulationsWithStation = <T extends SubSimulationLike>(
  subSimulations: T[],
  closestStation: WeatherStation | null,
  previousStation: WeatherStation | null = null
): T[] => {
  if (!Array.isArray(subSimulations) || !closestStation) return subSimulations;

  return subSimulations.map((subSim) =>
    subSim?.simType === 'WIND'
      ? {
          ...subSim,
          params: {
            ...subSim.params,
            referenceHeight: shouldUpdateStationDerivedValue(
              subSim?.params?.referenceHeight,
              previousStation?.measurementHeight
            )
              ? closestStation.measurementHeight
              : subSim?.params?.referenceHeight,
            flowDir: shouldUpdateStationDerivedValue(subSim?.params?.flowDir, previousStation?.avgDir)
              ? closestStation.avgDir
              : subSim?.params?.flowDir,
            referenceVelocity: shouldUpdateStationDerivedValue(
              subSim?.params?.referenceVelocity,
              previousStation?.avgYear
            )
              ? closestStation.avgYear
              : subSim?.params?.referenceVelocity,
          },
        }
      : subSim
  );
};

const hydrateWindSubSimulationsFromGrid = <T extends SubSimulationLike>(
  subSimulations: T[],
  grid: GridLike | null | undefined
) => {
  const closestStation = getClosestStationFromGrid(grid);
  return hydrateWindSubSimulationsWithStation(subSimulations, closestStation);
};

export { getClosestStationFromGrid, hydrateWindSubSimulationsWithStation, hydrateWindSubSimulationsFromGrid };
